<template>
  <div>
    <NavBar>
      <BNavbarBrand v-if="!album">Network Gallery</BNavbarBrand>
      <BNavbarNav v-else class="overflow-hidden">
        <BNavItem @click.prevent="sidebarShow = !sidebarShow"><Icon icon="bi:list" style="font-size:1.5em" /></BNavItem>
        <BNavItem to="/"><Icon icon="bi:chevron-left" style="font-size:1.5em" /></BNavItem>
        <BNavbarBrand class="text-nowrap overflow-hidden" :title="album.name">{{ album.name }}</BNavbarBrand>
      </BNavbarNav>
    </NavBar>
    <BOffcanvas v-if="album" id="album-sidebar" v-model="sidebarShow" :title="album.name"
               placement="start" bodyScrolling
               @show="exitFullscreen()">
      <BNav class="px-3" vertical>
        <BNavItem @click="startSlideshow()" class="mt-3" linkClass="btn btn-primary"><Icon icon="bi:play-fill" /> Eine Slideshow Starten</BNavItem>
        <BNavItem @click="startSlideshow(undefined, true)" class="mt-3" linkClass="btn btn-primary"><Icon icon="bi:shuffle" class="mr-3" /> Zufällige Slideshow</BNavItem>
        <BNavText class="mt-3"><Icon icon="bi:sort-down-alt" /> Sortierung</BNavText>
        <BNavForm>
          <BFormRadioGroup v-model="sortOrder" name="radio-btn-stacked" buttons stacked>
            <BFormRadio :value="[{col:'shot',dir:'asc'}]"><Icon icon="bi:calendar-date" /><Icon icon="bi:sort-numeric-down" /> Aufnahmedatum</BFormRadio>
            <BFormRadio :value="[{col:'shot',dir:'desc'}]"><Icon icon="bi:calendar-date" /><Icon icon="bi:sort-numeric-up" /> Aufnahmedatum</BFormRadio>
            <BFormRadio :value="[{col:'rating',dir:'desc'}]"><Icon icon="bi:star-fill" /><Icon icon="bi:sort-numeric-down-alt" /> Bewertung</BFormRadio>
          </BFormRadioGroup>
        </BNavForm>
        <BNavText class="mt-3"><Icon icon="bi:funnel-fill" /> Filter</BNavText>
        <BNavForm>
          <BFormGroup>
            <BInputGroup>
              <BFormInput placeholder="Search"></BFormInput>
              <template #append>
                <BButton variant="primary">Ok</BButton>
              </template>
            </BInputGroup>
          </BFormGroup>
          <BFormGroup>
            <BFormInput type="date" placeholder="Aufnahmedatum"></BFormInput>
          </BFormGroup>
        </BNavForm>
      </BNav>
      <template #footer>
        <form style="padding: 1rem">
          <BFormGroup>
            <template #label><Icon icon="bi:clock-history" /> Slideshow Geschwindigkeit</template>
            <BInputGroup>
              <BFormInput type="range" min="2" max="64" step="1"
                          @change="setSlideshowSpeed()"
                          v-model="nextSlideshowSpeed"/>
              <BInputGroupText>{{ nextSlideshowSpeed }} seconds</BInputGroupText>
            </BInputGroup>
          </BFormGroup>
        </form>
      </template>
    </BOffcanvas>
    <div class="album-gallery">
      <Loader :is-loading="isLoading"/>
      <div v-if="album" class="gallery">
        <div class="image__container" v-for="image in sortedImages" :key="image.id">
          <ImageContainer class="image" :source="imageSource(image.id)" @click="showImage(image.id)" />
          <div class="image__caption">
            <div class="image__status">
              <Icon v-if="image.versions.length > 1" icon="bi:collection" />
            </div>
            <div class="image__title">
              {{image.title}} <small v-if="image.description"> - {{image.description}}</small>
            </div>
            <div class="image__date">
              <image-property icon="date" label="Aufnahmedatum" no-text :value="formatDate(image)"/>
            </div>
            <div class="image__menu-container">
              <BDropdown toggleClass="image__menu-toggle text-decoration-none"
                          size="lg" variant="link" noCaret end>
                <template #button-content><Icon icon="bi:list" /></template>
                <BDropdownItem @click="startSlideshow(image)"><Icon icon="bi:play" /> &nbsp;Start slideshow from here</BDropdownItem>
                <BDropdownItem><Icon icon="bi:collection" /> &nbsp;Merge Into</BDropdownItem>
                <BDropdownItem><Icon icon="bi:trash" /> &nbsp;Remove Image</BDropdownItem>
              </BDropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ImageDetails ref="imageDetailRef" v-if="displayedImage"
                  :images="sortedImages"
                  :start-image="displayedImage"
                  @switched-image="switchedImage($event)"
                  @close-image="closeImage()"
                  @entered-fullscreen="closeSidebar()"
                  @toggle-sidebar="sidebarShow = !sidebarShow"
    ></ImageDetails>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import ImageContainer from '@/components/ImageContainer.vue'
import ImageDetails from '@/components/ImageDetails.vue'
import ImageProperty from '@/components/ImageProperty.vue'
import Loader from '@/components/Loader.vue'
import NavBar from '@/components/NavBar.vue'
import { Icon } from '@iconify/vue'
import {
  BOffcanvas, BNavbarBrand, BNavbarNav, BNavItem, BNavText, BNav, BNavForm,
  BFormRadioGroup, BFormRadio, BFormGroup,
  BInputGroup, BInputGroupText, BFormInput, BButton,
  BDropdown, BDropdownItem,
} from 'bootstrap-vue-next'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import axios from 'axios'
import AlbumModel from '@/models/Album'
import Image from '@/models/Image'
import { windowWidth, slideshowSpeed, setSlideshowSpeed as setSpeed, setSubPageTitle } from '@/stores/ui'

const route = useRoute()
const router = useRouter()

const album = ref<AlbumModel | null>(null)
const displayedImage = ref<Image | null>(null)
const isLoading = ref(false)
let loader: Promise<void> | null = null
const routes: string[] = []
const nextSlideshowSpeed = ref(slideshowSpeed.value / 1000)
const sidebarShow = ref(false)
const imageDetailRef = ref<InstanceType<typeof ImageDetails> | null>(null)
const sortOrder = ref<Array<{col: string; dir: string}>>([{ col: 'shot', dir: 'asc' }])

const sortedImages = computed(() => {
  if (!album.value) return []
  const { col, dir } = sortOrder.value[0]
  return [...album.value.images].sort((a, b) => {
    const av = String((a as any)[col] ?? '')
    const bv = String((b as any)[col] ?? '')
    return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
  })
})

onBeforeRouteUpdate((to) => {
  routes.push(to.fullPath as string)
  displayedImage.value = to.query.image
    ? album.value?.images.find(img => img.id === to.query.image) ?? null
    : null
})

onMounted(() => {
  routes.push(route.fullPath)
  refreshAlbum()
})

function refreshAlbum() {
  if (!loader) {
    isLoading.value = true
    loader = axios.get('/api/albums/' + route.params.albumId + '/images').then(response => {
      loader = null
      album.value = response.data
      setSubPageTitle(album.value?.name ?? '')
      if (route.query.image) {
        displayedImage.value = album.value?.images.find(img => img.id === route.query.image) ?? null
      }
      isLoading.value = false
    }).catch(reason => {
      loader = null
      console.log(reason)
      isLoading.value = false
    })
  }
}

function setSlideshowSpeed() {
  setSpeed(Number(nextSlideshowSpeed.value) * 1000)
}

function imageSource(imageId: string) {
  return windowWidth.value > 576
    ? '/api/images/' + imageId + '/thumbnail.jpg?height=300'
    : '/api/images/' + imageId + '/thumbnail.jpg?width=576'
}

function switchedImage({ image }: { image: Image }) {
  router[displayedImage.value ? 'replace' : 'push']('/album/' + album.value?.id + '?image=' + image.id)
}

function showImage(imageId: string) {
  router[displayedImage.value ? 'replace' : 'push']('/album/' + album.value?.id + '?image=' + imageId)
}

function exitFullscreen() {
  imageDetailRef.value?.exitFullscreen()
}

function closeSidebar() {
  sidebarShow.value = false
}

function startSlideshow(image?: Image, random = false) {
  if (!album.value) return
  image = image || sortedImages.value[0]
  if (!image) return
  if (!imageDetailRef.value || displayedImage.value !== image) {
    router.push('/album/' + album.value.id + '?image=' + image.id).then(() => {
      imageDetailRef.value?.startSlideshow(random)
    })
  } else {
    imageDetailRef.value.startSlideshow(random)
  }
}

function closeImage() {
  const p = routes.indexOf('/album/' + album.value?.id)
  if (p > -1) router.go(-1)
  else router.push('/album/' + album.value?.id)
}

function formatDate(image: Image): string {
  return format(new Date(image.shot), 'PPp', { locale: de })
}
</script>

<style lang="scss">
@import 'src/main.scss';

.album-gallery {
  padding-top: 56px;
  min-height: calc(100vh - 258px);

  .gallery {
    display: flex;
    flex: auto 1 1;
    flex-wrap: wrap;
    flex-direction: column;

    .image__container {
      width: calc(100% - 1em);
      margin: 0.5em;
      background: $card-bg;
      border-radius: 10px;
      min-width: 220px;
      position: relative;
    }

    .image-container {
      width: 100%;
      background: #000;
      text-align: center;
      vertical-align: middle;
      margin-bottom: 1em;
      border-radius: 10px 10px 0 0;
      overflow: hidden;
      cursor: pointer;

      > img {
        max-width: 100%;
        max-height: 400px;
      }
    }

    .image__caption {
      height: calc(1em + 24px + 64px);
      padding: 1em;
    }

    .image__title {
      color: #fff;
      font-size: 90%;
      width: calc(100% - 108px);
      max-width: 262px; // 400px width image
      line-height: 1.2;
      max-height: calc(24px + 64px - 18px - 14px);
      overflow: hidden;
    }

    .image__status {
      float: right;
      width: 108px;
      overflow: hidden;
      height: 24px;
      text-align: right;
      padding: 4px;
      line-height: 1;
    }

    .image__date {
      color: #777;
      float: left;
      font-size: 80%;
      position: absolute;
      bottom: 1em;
    }

    .image__menu-container {
      position: absolute;
      right: 0;
      bottom: 0;
      height: 64px;
      width: 64px;

      :deep(.dropdown) {
        height: 100%;
      }
    }

    .image__menu-toggle {
      padding: 0;
      width: 64px;
      height: 64px;
      text-align: center;
      line-height: 64px;
    }

    @include media-breakpoint-up(md) {
      flex-direction: row;
      justify-content: space-between;

      .image__container {
        max-width: calc(50% - 1em);
        width: auto;
      }

      .image-container {
        height: 300px;
        position: relative;
        margin: 0;

        > img {
          position: relative;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }

    @include media-breakpoint-up(lg)  {
      .image__container {
        max-width: calc(33% - 1em);
        width: auto;
      }
    }
  }
}

</style>
